/*
 * @format
 *
 * This reducer manages draft schedule changes that are stored locally
 * before being submitted to the API.
 *
 * Key distinction from pendingSchedule:
 * - draftSchedule = changes that are LOCAL ONLY (not yet sent to API)
 * - pendingSchedule = changes that are IN-FLIGHT (already sent to API, waiting for confirmation)
 *
 * Draft schedules allow users to make multiple edits to a schedule
 * and then save all changes at once with a single API call.
 */

import * as actionTypes from '../action-types';
import { cloneDeep } from 'lodash';

export const initialState = {
  drafts: [], // Array of { homeId, zoneDeviceId, endpointId, schedule, originalSchedule }
};

/**
 * Draft schedule reducer
 *
 * Handles:
 * - SET_DRAFT_SCHEDULE: Store/update draft for a zone
 * - CLEAR_DRAFT_SCHEDULE: Clear draft for a specific zone (discard)
 * - CLEAR_ALL_DRAFT_SCHEDULES: Clear all drafts
 * - SET_SCHEDULE_CONFIRMED: Clear draft after successful API submission
 */
export const draftSchedule = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_DRAFT_SCHEDULE: {
      const { homeId, zoneId, schedule, originalSchedule } = action;

      if (!zoneId || !Array.isArray(zoneId) || zoneId.length < 2) {
        console.warn('SET_DRAFT_SCHEDULE: Invalid zoneId', zoneId);
        return state;
      }

      const zoneDeviceId = zoneId[0];
      const endpointId = zoneId[1];

      // Remove any existing draft for this zone
      const filteredDrafts = state.drafts.filter(
        draft =>
          !(
            draft.homeId === homeId &&
            draft.zoneDeviceId === zoneDeviceId &&
            draft.endpointId === endpointId
          ),
      );

      // Add the new/updated draft
      // Deep clone to prevent state mutations when schedule helpers modify arrays
      return {
        ...state,
        drafts: [
          ...filteredDrafts,
          {
            homeId,
            zoneDeviceId,
            endpointId,
            schedule: cloneDeep(schedule),
            originalSchedule: cloneDeep(originalSchedule),
          },
        ],
      };
    }

    case actionTypes.CLEAR_DRAFT_SCHEDULE: {
      const { homeId, zoneId } = action;

      if (!zoneId || !Array.isArray(zoneId) || zoneId.length < 2) {
        console.warn('CLEAR_DRAFT_SCHEDULE: Invalid zoneId', zoneId);
        return state;
      }

      const zoneDeviceId = zoneId[0];
      const endpointId = zoneId[1];

      return {
        ...state,
        drafts: state.drafts.filter(
          draft =>
            !(
              draft.homeId === homeId &&
              draft.zoneDeviceId === zoneDeviceId &&
              draft.endpointId === endpointId
            ),
        ),
      };
    }

    case actionTypes.CLEAR_ALL_DRAFT_SCHEDULES: {
      return {
        ...state,
        drafts: [],
      };
    }

    // Clear draft when schedule is successfully submitted to API
    case actionTypes.SET_SCHEDULE_CONFIRMED: {
      const { homeId, zoneId } = action;

      if (!zoneId || !Array.isArray(zoneId) || zoneId.length < 2) {
        return state;
      }

      const zoneDeviceId = zoneId[0];
      const endpointId = zoneId[1];

      return {
        ...state,
        drafts: state.drafts.filter(
          draft =>
            !(
              draft.homeId === homeId &&
              draft.zoneDeviceId === zoneDeviceId &&
              draft.endpointId === endpointId
            ),
        ),
      };
    }

    default:
      return state;
  }
};

export default draftSchedule;
